import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { http } from "../../services/http";

/* ================= TYPES (match OrderDto của bạn) ================= */
type OrderDetail = {
  variantId: number;
  quantity: number;
  price: number;
  productName: string;
  imageUrl?: string | null;
  size?: string | null;
  color?: string | null;
  subtotal: number;
};

type OrderApi = {
  orderId: number;
  userId: number;
  orderDate: string;

  totalPrice: number;
  subtotal: number;

  statusId: number;

  guestId?: string | null;

  voucherCode?: string | null;
  discountAmount: number;
  shippingFee: number;
  loyaltyPointDiscount: number;

  // Shipping basic
  receiverName?: string | null;
  phone?: string | null;
  email?: string | null;
  streetAddress?: string | null;
  cityCode?: string | null;
  districtCode?: string | null;
  wardCode?: string | null;

  orderDetails: OrderDetail[];

  paymentMethod?: string | null;
  paymentStatus?: string | null;
};

/* ================= HELPERS ================= */
function safeText(v?: string | null, fallback = "—") {
  return v && String(v).trim() ? String(v).trim() : fallback;
}

function buildAddress(o: OrderApi) {
  const parts = [o.streetAddress, o.wardCode, o.districtCode, o.cityCode].filter(
    (x) => x && String(x).trim(),
  ) as string[];
  return parts.length ? parts.join(", ") : "—";
}

function Card({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={[
        "rounded-3xl border border-black/5 bg-white/70 backdrop-blur",
        "shadow-[0_10px_40px_-20px_rgba(0,0,0,0.35)]",
        className,
      ].join(" ")}
    >
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6 py-2">
      <p className="text-sm text-neutral-500">{label}</p>
      <div className="text-sm text-neutral-900 text-right">{value}</div>
    </div>
  );
}

/* ================= PAGE ================= */
export default function OrderDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();

  const lang = i18n.language?.startsWith("en") ? "en" : "vi";
  const moneyLocale = lang === "en" ? "en-US" : "vi-VN";

  const formatMoney = (n: number) =>
    (Number.isFinite(n) ? n : 0).toLocaleString(moneyLocale, {
      maximumFractionDigits: 0,
    }) + "₫";

  const formatDateTime = (d?: string | Date | null) => {
    if (!d) return "—";
    const dt = typeof d === "string" ? new Date(d) : d;
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleString(moneyLocale);
  };

  const statusLabel = (statusId: number) => {
    // map theo backend
    const vi: Record<number, string> = {
      6: t("orderDetail.status.pending"),
      7: t("orderDetail.status.confirmed"),
      8: t("orderDetail.status.processing"),
      9: t("orderDetail.status.delivered"),
      10: t("orderDetail.status.cancelled"),
      11: t("orderDetail.status.rejected"),
      12: t("orderDetail.status.completed"),
      13: t("orderDetail.status.returned"),
    };

    return vi[statusId] ?? t("orderDetail.statusFallback", { id: statusId });
  };

  const statusBadgeCls = (statusId: number) => {
    if (statusId === 6) return "bg-amber-50 text-amber-700 ring-amber-200";
    if (statusId === 7) return "bg-sky-50 text-sky-700 ring-sky-200";
    if (statusId === 8) return "bg-indigo-50 text-indigo-700 ring-indigo-200";
    if (statusId === 9) return "bg-emerald-50 text-emerald-700 ring-emerald-200";
    if (statusId === 10) return "bg-rose-50 text-rose-700 ring-rose-200";
    if (statusId === 11) return "bg-rose-50 text-rose-700 ring-rose-200";
    if (statusId === 12) return "bg-emerald-50 text-emerald-700 ring-emerald-200";
    if (statusId === 13) return "bg-violet-50 text-violet-700 ring-violet-200";
    return "bg-neutral-50 text-neutral-600 ring-neutral-200";
  };

  const [data, setData] = useState<OrderApi | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setErr("");

    http
      .get(`/api/Order/${id}`)
      .then((res) => {
        if (!mounted) return;
        setData(res?.data ?? null);
      })
      .catch(() => mounted && setErr(t("orderDetail.errorLoad")))
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [id, t]);

  const itemsCount = useMemo(() => {
    if (!data?.orderDetails?.length) return 0;
    return data.orderDetails.reduce((s, it) => s + (it.quantity ?? 0), 0);
  }, [data]);

  const itemsTotal = useMemo(() => {
    if (!data?.orderDetails?.length) return 0;
    return data.orderDetails.reduce(
      (s, it) => s + (it.subtotal ?? (it.price ?? 0) * (it.quantity ?? 0)),
      0,
    );
  }, [data]);

  const computedTotal = useMemo(() => {
    if (!data) return 0;
    const sub = data.subtotal ?? itemsTotal;
    const ship = data.shippingFee ?? 0;
    const disc = data.discountAmount ?? 0;
    const pointDisc = data.loyaltyPointDiscount ?? 0;
    return sub + ship - disc - pointDisc;
  }, [data, itemsTotal]);

  if (loading) {
    return (
      <div className="min-h-[70vh] bg-[#f6f3ee]">
        <div className="max-w-6xl mx-auto px-4 py-16 text-center text-sm text-neutral-500">
          {t("orderDetail.loading")}
        </div>
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-[70vh] bg-[#f6f3ee]">
        <div className="max-w-6xl mx-auto px-4 py-16 text-center">
          <p className="text-sm text-rose-600">{err || t("orderDetail.noData")}</p>
          <div className="mt-4 flex justify-center gap-2">
            <Link
              to="/profile"
              className="rounded-2xl px-4 py-2 text-sm font-medium border border-black/10 bg-white hover:bg-black hover:text-white transition"
            >
              {t("orderDetail.backToAccount")}
            </Link>
            <button
              onClick={() => window.location.reload()}
              className="rounded-2xl px-4 py-2 text-sm font-medium bg-black text-white hover:opacity-90 transition"
            >
              {t("orderDetail.reload")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const st = statusLabel(data.statusId);

  return (
    <div className="min-h-[70vh] bg-[#f6f3ee]">
      {/* HEADER */}
      <div className="max-w-6xl mx-auto px-4 pt-4 pb-4">
        <p className="text-[11px] uppercase tracking-[0.45em] text-neutral-600">
          {t("orderDetail.brandKicker")}
        </p>

        <div className="mt-3 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-neutral-900">
                {t("orderDetail.title", { id: data.orderId })}
              </h1>
              <span
                className={[
                  "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset",
                  statusBadgeCls(data.statusId),
                ].join(" ")}
              >
                {st}
              </span>
            </div>

            <p className="mt-2 text-sm text-neutral-600">
              {t("orderDetail.orderedAt")}{" "}
              <span className="text-neutral-900 font-medium">
                {formatDateTime(data.orderDate)}
              </span>
            </p>

            {Math.abs((data.totalPrice ?? 0) - computedTotal) > 1 && (
              <p className="mt-1 text-xs text-neutral-500">
                {t("orderDetail.totalFromBackendHint")}
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <Link
              to="/profile"
              className="rounded-2xl px-4 py-2.5 text-sm font-medium border border-black/10 bg-white hover:bg-black hover:text-white transition"
            >
              {t("orderDetail.back")}
            </Link>
            <Link
              to="/shop"
              className="rounded-2xl px-4 py-2.5 text-sm font-medium bg-black text-white hover:opacity-90 transition"
            >
              {t("orderDetail.continueShopping")}
            </Link>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* LEFT: ITEMS */}
          <Card className="p-6 lg:col-span-2">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-neutral-900">
                {t("orderDetail.itemsTitle")}
              </h2>
              <p className="text-sm text-neutral-600">
                {t("orderDetail.itemsCount", { count: itemsCount })}
              </p>
            </div>

            <div className="mt-5 space-y-4">
              {data.orderDetails?.map((it, idx) => (
                <div
                  key={`${it.variantId}-${idx}`}
                  className="rounded-3xl border border-black/5 bg-white p-5"
                >
                  <div className="flex gap-4">
                    {it.imageUrl ? (
                      <img
                        src={it.imageUrl}
                        alt={it.productName}
                        className="h-20 w-20 rounded-2xl object-cover ring-1 ring-black/10"
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-2xl bg-black/5 ring-1 ring-black/10" />
                    )}

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-neutral-900 truncate">
                        {it.productName}
                      </p>

                      <p className="mt-1 text-sm text-neutral-600">
                        {t("orderDetail.color")}{" "}
                        <span className="text-neutral-900">
                          {safeText(it.color)}
                        </span>
                        <span className="mx-2 text-neutral-300">•</span>
                        {t("orderDetail.size")}{" "}
                        <span className="text-neutral-900">
                          {safeText(it.size)}
                        </span>
                        <span className="mx-2 text-neutral-300">•</span>
                        {t("orderDetail.qty")}{" "}
                        <span className="text-neutral-900">{it.quantity}</span>
                      </p>

                      <div className="mt-3 flex items-end justify-between gap-4">
                        <div className="text-sm text-neutral-600">
                          {t("orderDetail.unitPrice")}{" "}
                          <span className="text-neutral-900 font-medium">
                            {formatMoney(it.price)}
                          </span>
                        </div>

                        <div className="text-right">
                          <p className="text-xs text-neutral-500">
                            {t("orderDetail.lineTotal")}
                          </p>
                          <p className="text-base font-semibold text-neutral-900">
                            {formatMoney(it.subtotal ?? it.price * it.quantity)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {!data.orderDetails?.length && (
                <div className="rounded-3xl border border-dashed border-black/15 bg-white p-10 text-center">
                  <p className="text-sm text-neutral-600">
                    {t("orderDetail.noItems")}
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* RIGHT */}
          <div className="space-y-6">
            {/* SHIPPING */}
            <Card className="p-6">
              <h3 className="text-base font-semibold text-neutral-900">
                {t("orderDetail.shippingTitle")}
              </h3>

              <div className="mt-4 divide-y divide-black/5">
                <Row
                  label={t("orderDetail.receiver")}
                  value={<span className="font-medium">{safeText(data.receiverName)}</span>}
                />
                <Row label={t("orderDetail.phone")} value={safeText(data.phone)} />
                <Row label={t("orderDetail.email")} value={safeText(data.email)} />
                <Row
                  label={t("orderDetail.address")}
                  value={<span className="leading-relaxed">{buildAddress(data)}</span>}
                />
              </div>

              {!data.receiverName && !data.streetAddress && (
                <p className="mt-3 text-xs text-neutral-500">
                  {t("orderDetail.noShippingDataHint")}
                </p>
              )}
            </Card>

            {/* PAYMENT */}
            <Card className="p-6">
              <h3 className="text-base font-semibold text-neutral-900">
                {t("orderDetail.paymentTitle")}
              </h3>

              <div className="mt-4 divide-y divide-black/5">
                <Row
                  label={t("orderDetail.paymentMethod")}
                  value={(data.paymentMethod ? data.paymentMethod : "—").toUpperCase()}
                />
                <Row
                  label={t("orderDetail.paymentStatus")}
                  value={safeText(data.paymentStatus)}
                />
              </div>
            </Card>

            {/* SUMMARY */}
            <Card className="p-6">
              <h3 className="text-base font-semibold text-neutral-900">
                {t("orderDetail.summaryTitle")}
              </h3>

              <div className="mt-4">
                <div className="divide-y divide-black/5">
                  <Row
                    label={t("orderDetail.subtotal")}
                    value={formatMoney(data.subtotal ?? itemsTotal)}
                  />

                  <Row
                    label={t("orderDetail.shippingFee")}
                    value={
                      (data.shippingFee ?? 0) > 0
                        ? formatMoney(data.shippingFee ?? 0)
                        : t("orderDetail.freeShipping") // ✅ chuẩn freeship
                    }
                  />

                  <Row
                    label={t("orderDetail.voucher")}
                    value={
                      data.voucherCode ? (
                        <span className="font-medium">
                          {data.voucherCode}
                          {(data.discountAmount ?? 0) > 0 ? (
                            <span className="text-neutral-500 font-normal">
                              {" "}
                              (−{formatMoney(data.discountAmount ?? 0)})
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        "—"
                      )
                    }
                  />

                  <Row
                    label={t("orderDetail.points")}
                    value={
                      (data.loyaltyPointDiscount ?? 0) > 0
                        ? `−${formatMoney(data.loyaltyPointDiscount)}`
                        : "—"
                    }
                  />
                </div>

                <div className="mt-4 rounded-2xl bg-black text-white p-4 flex items-end justify-between">
                  <div>
                    <p className="text-xs text-white/70">{t("orderDetail.totalPay")}</p>
                    <p className="text-xl font-semibold">{formatMoney(data.totalPrice)}</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
